
import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Home, Camera, User, Mail, Globe, List, ThumbsUp, Flag, Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/hooks/useLanguage';
import { useIsMobile } from '@/hooks/use-mobile';

export const Navigation = () => {
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const { language, toggleLanguage, t } = useLanguage();
  const [logoHover, setLogoHover] = useState(false);
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsVisible(currentScrollY < lastScrollY || currentScrollY < 100);
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const scrollTo = (id: string) => {
    if (isHomePage) {
      const element = document.getElementById(id);
      element?.scrollIntoView({ behavior: 'smooth' });
      if (mobileMenuOpen) setMobileMenuOpen(false);
    } else {
      // If not on home page, navigate to home with hash
      window.location.href = `/#${id}`;
    }
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLanguageSwitch = (lang: string) => {
    if (language !== lang) {
      toggleLanguage();
    }
  };

  return (
    <nav className={cn('nav-menu', !isVisible && 'nav-hidden')}>
      <div className="container mx-auto px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="logo-container">
            <Link 
              to="/" 
              className="flex items-center"
              onMouseEnter={() => setLogoHover(true)}
              onMouseLeave={() => setLogoHover(false)}
            >
              <img 
                src="/logo.svg" 
                alt="meyf-art" 
                className={cn(
                  "h-10 w-auto transition-transform duration-500",
                  logoHover && "animate-spin-slow"
                )} 
              />
              <span className="ml-2 text-xl font-bold">meyf-art</span>
            </Link>
          </div>
          
          {isMobile ? (
            <>
              <button 
                onClick={toggleMobileMenu} 
                className="text-primary p-2"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              
              {mobileMenuOpen && (
                <div className="fixed inset-0 top-[72px] bg-background/95 backdrop-blur-md z-50">
                  <div className="flex flex-col space-y-4 p-6 bg-white/80 backdrop-blur-lg rounded-lg shadow-lg m-4">
                    {isHomePage ? (
                      <>
                        <button
                          onClick={() => scrollTo('home')}
                          className="mobile-nav-link flex items-center text-lg py-2"
                        >
                          <Home className="h-5 w-5 mr-2" />
                          <span>{t('Home')}</span>
                        </button>
                        <button
                          onClick={() => scrollTo('portfolio')}
                          className="mobile-nav-link flex items-center text-lg py-2"
                        >
                          <Camera className="h-5 w-5 mr-2" />
                          <span>{t('Portfolio')}</span>
                        </button>
                        <button
                          onClick={() => scrollTo('services')}
                          className="mobile-nav-link flex items-center text-lg py-2"
                        >
                          <List className="h-5 w-5 mr-2" />
                          <span>{t('Services')}</span>
                        </button>
                        <button
                          onClick={() => scrollTo('advantages')}
                          className="mobile-nav-link flex items-center text-lg py-2"
                        >
                          <ThumbsUp className="h-5 w-5 mr-2" />
                          <span>{t('Advantages')}</span>
                        </button>
                        <button
                          onClick={() => scrollTo('about')}
                          className="mobile-nav-link flex items-center text-lg py-2"
                        >
                          <User className="h-5 w-5 mr-2" />
                          <span>{t('About')}</span>
                        </button>
                      </>
                    ) : (
                      <>
                        <Link to="/" className="mobile-nav-link flex items-center text-lg py-2">
                          <Home className="h-5 w-5 mr-2" />
                          <span>{t('Home')}</span>
                        </Link>
                        <Link to="/#portfolio" className="mobile-nav-link flex items-center text-lg py-2">
                          <Camera className="h-5 w-5 mr-2" />
                          <span>{t('Portfolio')}</span>
                        </Link>
                        <Link to="/#services" className="mobile-nav-link flex items-center text-lg py-2">
                          <List className="h-5 w-5 mr-2" />
                          <span>{t('Services')}</span>
                        </Link>
                        <Link to="/#advantages" className="mobile-nav-link flex items-center text-lg py-2">
                          <ThumbsUp className="h-5 w-5 mr-2" />
                          <span>{t('Advantages')}</span>
                        </Link>
                        <Link to="/#about" className="mobile-nav-link flex items-center text-lg py-2">
                          <User className="h-5 w-5 mr-2" />
                          <span>{t('About')}</span>
                        </Link>
                      </>
                    )}
                    <Link to="/contact" className="mobile-nav-link contact-link flex items-center text-lg py-2">
                      <Mail className="h-5 w-5 mr-2 contact-icon-pulse" />
                      <span>{t('Contact')}</span>
                    </Link>
                    <div className="flex rounded-md overflow-hidden mt-2">
                      <button 
                        onClick={() => handleLanguageSwitch('en')}
                        className={cn(
                          "flex items-center justify-center py-2 px-3 flex-1",
                          language === 'en' 
                            ? "bg-primary text-white font-medium" 
                            : "bg-primary/10 hover:bg-primary/20"
                        )}
                        aria-label="Switch to English"
                      >
                        <span>EN</span>
                      </button>
                      <button 
                        onClick={() => handleLanguageSwitch('de')}
                        className={cn(
                          "flex items-center justify-center py-2 px-3 flex-1",
                          language === 'de' 
                            ? "bg-primary text-white font-medium" 
                            : "bg-primary/10 hover:bg-primary/20"
                        )}
                        aria-label="Switch to German"
                      >
                        <span>DE</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex space-x-6">
              {isHomePage ? (
                <>
                  <button
                    onClick={() => scrollTo('home')}
                    className="nav-link"
                  >
                    <Home className="h-4 w-4 mr-1" />
                    <span>{t('Home')}</span>
                  </button>
                  <button
                    onClick={() => scrollTo('portfolio')}
                    className="nav-link"
                  >
                    <Camera className="h-4 w-4 mr-1" />
                    <span>{t('Portfolio')}</span>
                  </button>
                  <button
                    onClick={() => scrollTo('services')}
                    className="nav-link"
                  >
                    <List className="h-4 w-4 mr-1" />
                    <span>{t('Services')}</span>
                  </button>
                  <button
                    onClick={() => scrollTo('advantages')}
                    className="nav-link"
                  >
                    <ThumbsUp className="h-4 w-4 mr-1" />
                    <span>{t('Advantages')}</span>
                  </button>
                  <button
                    onClick={() => scrollTo('about')}
                    className="nav-link"
                  >
                    <User className="h-4 w-4 mr-1" />
                    <span>{t('About')}</span>
                  </button>
                </>
              ) : (
                <>
                  <Link to="/" className="nav-link">
                    <Home className="h-4 w-4 mr-1" />
                    <span>{t('Home')}</span>
                  </Link>
                  <Link to="/#portfolio" className="nav-link">
                    <Camera className="h-4 w-4 mr-1" />
                    <span>{t('Portfolio')}</span>
                  </Link>
                  <Link to="/#services" className="nav-link">
                    <List className="h-4 w-4 mr-1" />
                    <span>{t('Services')}</span>
                  </Link>
                  <Link to="/#advantages" className="nav-link">
                    <ThumbsUp className="h-4 w-4 mr-1" />
                    <span>{t('Advantages')}</span>
                  </Link>
                  <Link to="/#about" className="nav-link">
                    <User className="h-4 w-4 mr-1" />
                    <span>{t('About')}</span>
                  </Link>
                </>
              )}
              <Link to="/contact" className="nav-link contact-link group">
                <div className="relative">
                  <Mail className="h-4 w-4 mr-1 contact-icon-pulse" />
                </div>
                <span className="relative overflow-hidden">
                  <span className="inline-block transition-transform duration-300 group-hover:-translate-y-full">
                    {t('Contact')}
                  </span>
                  <span className="absolute top-0 left-0 inline-block transition-transform duration-300 translate-y-full group-hover:translate-y-0">
                    {t('Contact')}
                  </span>
                </span>
              </Link>
              <div className="flex rounded-md overflow-hidden">
                <button 
                  onClick={() => handleLanguageSwitch('en')}
                  className={cn(
                    "flex items-center justify-center py-1 px-3",
                    language === 'en' 
                      ? "bg-primary text-white font-medium" 
                      : "bg-primary/10 hover:bg-primary/20"
                  )}
                  aria-label="Switch to English"
                >
                  <span>EN</span>
                </button>
                <button 
                  onClick={() => handleLanguageSwitch('de')}
                  className={cn(
                    "flex items-center justify-center py-1 px-3",
                    language === 'de' 
                      ? "bg-primary text-white font-medium" 
                      : "bg-primary/10 hover:bg-primary/20"
                  )}
                  aria-label="Switch to German"
                >
                  <span>DE</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};
